/**
 * Как показать фирменный логотип тайтла на тёмном фоне.
 *
 * - `plain` — как есть, в родных цветах. Так показываются ВСЕ цветные
 *   логотипы: красный, золотой, синий — это часть фирменного стиля тайтла.
 * - `glow` — тоже в родных цветах, но с мягкой светлой подсветкой позади:
 *   умеренно тёмный цветной логотип (тёмно-синий, бордовый) иначе тонет в
 *   ночном кадре, а перекрашивать его нельзя.
 * - `silhouette` — белым силуэтом. Только почти чёрный И почти бесцветный
 *   логотип: чёрные буквы на тёмном фоне не видны вовсе, и форма букв тут
 *   важнее их цвета, которого, по сути, нет.
 *
 * - `boxed` — не логотип, а картинка-плашка: прозрачного поля нет вовсе
 *   (PNG без альфы — буквы на сплошном фоне). Показывать её нельзя ни как
 *   есть (прямоугольник поверх кадра), ни силуэтом (сплошная белая полоса —
 *   так выглядело название «After Impact»): вместо неё — текст.
 *
 * Раньше белым силуэтом выводился любой логотип темнее порога по средней
 * яркости — и тёмно-красные, синие логотипы становились чёрно-белыми.
 */
export type LogoTone = 'plain' | 'glow' | 'silhouette' | 'boxed';

/**
 * Ниже этой средней светлоты (HSL: середина между самым ярким и самым тёмным
 * каналом) логотип на ночном кадре читается плохо. Светлота, а не яркость
 * для глаза: у чистого красного яркость низкая (зелёного в нём нет), но на
 * тёмном фоне он горит — перекрашивать или подсвечивать его незачем.
 */
const DARK = 0.3;
/** Ниже этой средней насыщенности логотип — «серый»: чёрный, графит. */
const NEUTRAL = 0.12;
/** Пиксель с меньшей непрозрачностью — край сглаживания, а не буква. */
const OPAQUE = 0.5;

/**
 * Тон логотипа по пикселям его маленькой копии (RGBA подряд, как в
 * `ImageData.data`). Считаются только непрозрачные пиксели: прозрачное поле
 * вокруг букв ни о чём не говорит. Насыщенность — средняя по пикселям, а не
 * насыщенность среднего цвета: у двухцветного «чёрный + красный» логотипа
 * средний цвет — бурый, а на деле половина букв ярко-красная.
 */
export function classifyLogo(pixels: ArrayLike<number>): LogoTone {
  let count = 0;
  let light = 0;
  let chroma = 0;
  // Пиксели, где картинка хоть сколько-то прозрачна. У настоящего логотипа их
  // много и в уменьшенной копии (поле вокруг букв, сглаженные края), у плашки
  // без альфы — ни одного.
  let seeThrough = 0;
  for (let index = 0; index + 3 < pixels.length; index += 4) {
    if (pixels[index + 3]! < 255) seeThrough += 1;
    const a = pixels[index + 3]! / 255;
    if (a < OPAQUE) continue;
    const r = pixels[index]! / 255;
    const g = pixels[index + 1]! / 255;
    const b = pixels[index + 2]! / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    light += (max + min) / 2;
    chroma += max - min;
    count += 1;
  }
  if (count === 0) return 'plain';
  if (seeThrough === 0) return 'boxed';
  light /= count;
  chroma /= count;
  if (light >= DARK) return 'plain';
  return chroma < NEUTRAL ? 'silhouette' : 'glow';
}

/** Ширина копии: тонкие буквы при 8 точках смазались бы в прозрачность. */
const SAMPLE_W = 48;

const cache = new Map<string, Promise<LogoTone>>();

/**
 * Тон логотипа по адресу; не вышло посчитать (нет холста, ошибка) — `plain`:
 * лучше родные цвета, чем ошибочный силуэт. Кэш по адресу.
 */
export function logoTone(url: string): Promise<LogoTone> {
  const known = cache.get(url);
  if (known) return known;
  const pending = measure(url).catch((): LogoTone => 'plain');
  cache.set(url, pending);
  return pending;
}

async function measure(url: string): Promise<LogoTone> {
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  await image.decode();
  const ratio = image.naturalWidth > 0 && image.naturalHeight > 0 ? image.naturalHeight / image.naturalWidth : 0.33;
  const width = SAMPLE_W;
  const height = Math.max(4, Math.min(SAMPLE_W * 2, Math.round(SAMPLE_W * ratio)));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return 'plain';
  context.drawImage(image, 0, 0, width, height);
  return classifyLogo(context.getImageData(0, 0, width, height).data);
}
